import type { PaletteOptions } from '@mui/material/styles';

export const DEFAULT_PARTY_BACKGROUND = '#240008';
export const isPartyColor = (color: string) => /^#[0-9a-f]{6}$/i.test(color);

// Preserve hue and saturation: every token moves only toward white in HSL lightness.
export function partyShade(background: string, amount: number): string {
  const hex = isPartyColor(background) ? background : DEFAULT_PARTY_BACKGROUND;
  const [r, g, b] = [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
  let hue = 0;
  if (delta !== 0) {
    if (max === r) hue = ((g - b) / delta) % 6;
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
  }
  hue = (hue * 60 + 360) % 360;
  const rounded = (value: number) => Number(value.toFixed(6));
  return `hsl(${rounded(hue)}, ${rounded(saturation * 100)}%, ${rounded((lightness + (1 - lightness) * amount) * 100)}%)`;
}

export function createPartyPalette(background: string): PaletteOptions {
  const base = isPartyColor(background) ? background : DEFAULT_PARTY_BACKGROUND;
  const shade = (amount: number) => partyShade(base, amount);
  const accent = (amount: number) => ({
    main: shade(amount),
    light: shade(Math.min(amount + 0.18, 0.95)),
    dark: shade(Math.max(amount - 0.12, 0)),
    contrastText: base,
  });

  // MUI also supplies a legacy text.icon token at runtime. Tint it too.
  const text = {
    primary: shade(0.95),
    secondary: shade(0.74),
    disabled: shade(0.42),
    icon: shade(0.74),
  };

  return {
    mode: 'dark',
    partyBackground: base,
    background: { default: base, paper: shade(0.06) },
    common: { black: base, white: shade(0.98) },
    grey: Object.fromEntries(
      [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 'A100', 'A200', 'A400', 'A700'].map(
        (key, index) => [key, shade(0.95 - index * 0.06)]
      )
    ),
    primary: accent(0.6),
    secondary: accent(0.76),
    error: accent(0.65),
    warning: accent(0.72),
    success: accent(0.58),
    info: accent(0.8),
    text,
    surface: shade(0.06),
    surfaceHover: shade(0.13),
    muted: shade(0.09),
    inputBorder: shade(0.3),
    border: shade(0.24),
    divider: shade(0.24),
    statusCard: {
      background: shade(0.1),
      border: shade(0.55),
      text: shade(0.92),
      muted: shade(0.72),
    },
    action: {
      active: shade(0.88),
      hover: shade(0.14),
      selected: shade(0.2),
      disabled: shade(0.4),
      disabledBackground: shade(0.1),
      focus: shade(0.28),
    },
  };
}
